import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '../account.service';
import { UpdatePassword } from '../UpdatePassword';
import { ErrorMessage } from '../../common/model/errorMessage';
import { Account } from '../account';
import { TranslateService } from '@ngx-translate/core';
import { passwordValidator } from '../passwordValidator';
import {ErrorCodesEnum} from '../../common/enums/errorCodesEnum';
import {Localization} from '../../common/enums/localization';
import {concatMap, delay, finalize, tap} from 'rxjs/operators';
import {AuthenticationService} from '../../common/services/authentication/authentication.service'
import {LicenseService} from "../../license/license.service";
import {noop, Observable, of} from "rxjs";
import {BusinessRulesService} from "../../../services/business-rules.service";
import {ProgressSpinnerMode} from '@angular/material/progress-spinner';

@Component({
  selector: 'app-account-confirm',
  templateUrl: './account-confirm.component.html',
  styleUrls: ['./account-confirm.component.scss'],
  providers: [AccountService]
})
export class AccountConfirmComponent implements OnInit {

  public accountConfirmGroup: UntypedFormGroup;
  public licenseContent: string = "";
  public sub: any;
  public culture: any;
  public confirmationCode: string;
  public account: Account;
  public progressSpinnerMode: ProgressSpinnerMode = 'indeterminate';
  public showSpinner: boolean = true;
  public translationCompleted: boolean = false;
  public agreedToTerms: boolean;
  public readonly Localization = Localization;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private authenticationService: AuthenticationService,
    private translate: TranslateService,
    private licenseService: LicenseService,
    private businessRules: BusinessRulesService,
  ) {}

  ngOnInit() {
    this.agreedToTerms = false;
    this.sub = this.route.params.subscribe(params => {
      this.culture = params['culture'];
      this.confirmationCode = params['confirmationCode'];

      if (this.culture == null) {
        this.culture == "en-US";
      }

      this.translate.use(this.culture)
        .pipe(
          tap(() => this.translationCompleted = true)
        )
        .subscribe(noop);

      this.getLicenseByCulture();

      this.accountService.getLoginByConfirmationCode(this.confirmationCode).subscribe(
        (res: Account) => {
          this.account = res;
          this.accountConfirmGroup.get('newPasswordRequired').setValue(this.account.newPasswordRequired);
          this.showSpinner = false;
        },
        err => {
          this.handleError(err.error);
        })
    });

    this.accountConfirmGroup = this.formBuilder.group({
      password: [],
      passwordConfirm: [],
      newPasswordRequired: [],
    }, { validator: passwordValidator });
  }

  public onAgreeToTermsChange(checked: boolean) {
    this.agreedToTerms = checked;
  }

  getLicenseByCulture() {
    this.licenseService.getLicense(this.culture)
      .subscribe(license => this.licenseContent = license)
  }

  onSubmit() {
    if (this.accountConfirmGroup.valid) {
      this.showSpinner = true;
        this.updatePassword().pipe(
          concatMap(() => this.agreeWithLicense()),
          finalize(() => this.showSpinner = false),
        ).subscribe(() => {
          this.hardRedirect();
        }, error => this.handleError(error))
      }
    }

  private agreeWithLicense() {
    return this.accountService.agreeLicenseOnly(this.confirmationCode);
  }

  private updatePassword(): Observable<Object|null> {
    if (this.account.newPasswordRequired) {
      let updatePassword = new UpdatePassword(this.confirmationCode, this.accountConfirmGroup.get('password').value);
      return this.accountService.updatePasswordAndAgreeLicense(updatePassword);
    }
    return of(null);
  }

  handleError(error: ErrorMessage) {
    if (error.code != null && error.code == ErrorCodesEnum.AccountAlreadyConfirmed) {
      this.router.navigateByUrl('/account-already-confirmed');
    }
    else {
      this.router.navigateByUrl('/account-error');
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  /**
   * Forces the application to reload
   * so permissions are properly loaded.
   * @private
   */
  private hardRedirect() {
    const base = document.baseURI;
    window.location.href = base;
  }
}
